import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { AnchorHTMLAttributes, ButtonHTMLAttributes, forwardRef } from 'react';
import styles from './button.module.scss';

type ButtonBaseProps = {
  withArrow?: boolean;
  label?: string;
  variant?: 'primary' | 'secondary';
  size?: 'default' | 'large';
};

export type ButtonProps = ButtonBaseProps &
  (
    | ({ href: string; newWindow?: boolean } & Omit<
        AnchorHTMLAttributes<HTMLAnchorElement>,
        'href'
      >)
    | ({ href?: never; newWindow?: never } & ButtonHTMLAttributes<HTMLButtonElement>)
  );

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      className,
      withArrow = false,
      children,
      variant = 'primary',
      size = 'default',
      href,
      newWindow,
      ...props
    },
    ref,
  ) => {
    const classes = cn(
      styles.base,
      styles[`base--${variant}`],
      size === 'large' && styles['base--large'],
      className,
    );

    const content = (
      <>
        <div className={styles.base_children}>{children}</div>
        {withArrow && <ArrowRight width={20} height={20} />}
      </>
    );

    if (href) {
      return (
        <a
          href={href}
          className={classes}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...(newWindow ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        className={classes}
        ref={ref as React.Ref<HTMLButtonElement>}
        {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    );
  },
);

Button.displayName = 'Button';
